import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih, formatTl, CARI_TIP_AD } from '../lib/api';
import { useAuth } from '../lib/auth';
import YeniCari from './YeniCari';

type Satir = {
  id: number; code: string | null; name: string; type: string; status: string;
  phone: string | null; city: string | null; segment: string | null;
  sorumlu: string | null; sonTemas: string | null; sonTemasGun: number | null;
  takipsiz: boolean; teklifAdedi: number; siparisAdedi: number; numuneAdedi: number;
  acikAlacakKurus?: number;
};
type Veri = { satirlar: Satir[]; takipsizGun: number; ozet: { toplam: number; takipsiz: number } };

/**
 * CARİLER — CRM eki 13.2.
 *
 * "Takipsiz" sütunu modülün varlık sebebi: 45 gündür konuşulmamış aktif cari
 * unutulmuş demektir ve bugün bunu kimse ölçmüyor.
 */
export default function Crm() {
  const { can } = useAuth();
  const [ara, setAra] = useState('');
  const [takipsiz, setTakipsiz] = useState(false);
  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const yukle = useCallback(() => {
    const q = new URLSearchParams({ ...(ara ? { ara } : {}), ...(takipsiz ? { takipsiz: '1' } : {}) });
    api<Veri>(`/api/crm/cariler?${q}`).then(setVeri).catch((e) => setHata((e as Error).message));
  }, [ara, takipsiz]);
  useEffect(() => {
    const t = setTimeout(yukle, ara ? 250 : 0);
    return () => clearTimeout(t);
  }, [yukle, ara]);

  if (hata && !veri) {
    return <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>;
  }

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Cariler</h1>
          <div className="ccd-meta">
            Son temas, açık teklif, numune ve bakiye tek ekranda
          </div>
        </div>
        {can('quote.write') && (
          <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setYeniAcik(true)} data-tur="yeni-cari">
            + Yeni Cari
          </button>
        )}
      </div>

      {veri && veri.ozet.takipsiz > 0 && !takipsiz && (
        <div className="ccd-banner ccd-banner-warning">
          <span className="ccd-banner-icon" aria-hidden="true">📵</span>
          <div>
            <div style={{ fontWeight: 600 }}>
              {veri.ozet.takipsiz} cari {veri.takipsizGun} gündür takipsiz
            </div>
            <div className="ccd-meta">
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setTakipsiz(true)}>
                Yalnız onları göster
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div className="ccd-tabs" role="tablist">
          <button role="tab" aria-selected={!takipsiz} className="ccd-tab"
                  onClick={() => setTakipsiz(false)}>Tümü</button>
          <button role="tab" aria-selected={takipsiz} className="ccd-tab"
                  onClick={() => setTakipsiz(true)} data-tur="sekme-takipsiz">Takipsiz</button>
        </div>
        <div className="ccd-search" style={{ minWidth: 240 }}>
          <span className="ccd-search-icon" aria-hidden="true">⌕</span>
          <input className="ccd-input" type="search" inputMode="search" enterKeyHint="search" autoComplete="off" placeholder="Cari adı, kod ya da telefon…"
                 value={ara} onChange={(e) => setAra(e.target.value)} aria-label="Cari ara" />
        </div>
      </div>

      {!veri ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : veri.satirlar.length === 0 ? (
        <div className="ccd-empty">
          {takipsiz ? 'Takipsiz cari yok. 👏' : ara ? `"${ara}" için sonuç yok.` : 'Henüz cari yok.'}
        </div>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">Cari</th>
                <th className="ccd-th">Son temas</th>
                <th className="ccd-th ccd-td-num">Teklif</th>
                <th className="ccd-th ccd-td-num">Sipariş</th>
                <th className="ccd-th ccd-td-num">Numune</th>
                {veri.satirlar.some((s) => s.acikAlacakKurus != null) && (
                  <th className="ccd-th ccd-td-num">Açık alacak</th>
                )}
                <th className="ccd-th">Sorumlu</th>
              </tr>
            </thead>
            <tbody>
              {veri.satirlar.map((c) => (
                <tr key={c.id} className="ccd-tr">
                  <td className="ccd-td">
                    <Link to={`/crm/${c.id}`} style={{ fontWeight: 600 }}>{c.name}</Link>
                    <div className="ccd-meta">
                      {c.code && <span className="ccd-mono">{c.code} · </span>}
                      {CARI_TIP_AD[c.type] ?? c.type}{c.city ? ` · ${c.city}` : ''}
                      {c.segment && ` · ${c.segment}`}
                    </div>
                  </td>
                  <td className="ccd-td ccd-td-nowrap">
                    {c.sonTemas ? (
                      <>
                        <div>{formatTarih(c.sonTemas)}</div>
                        <div className={`ccd-meta${c.takipsiz ? ' ccd-kpi-down' : ''}`}>
                          {c.sonTemasGun} gün önce
                        </div>
                      </>
                    ) : (
                      <span className="ccd-kpi-down">hiç temas yok</span>
                    )}
                  </td>
                  <td className="ccd-td ccd-td-num">{c.teklifAdedi}</td>
                  <td className="ccd-td ccd-td-num">{c.siparisAdedi}</td>
                  <td className="ccd-td ccd-td-num">{c.numuneAdedi}</td>
                  {c.acikAlacakKurus != null && (
                    <td className="ccd-td ccd-td-num">
                      {c.acikAlacakKurus > 0
                        ? <span className="ccd-kpi-down">{formatTl(c.acikAlacakKurus)}</span>
                        : <span className="ccd-muted">—</span>}
                    </td>
                  )}
                  <td className="ccd-td ccd-meta">{c.sorumlu ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {yeniAcik && (
        <YeniCari kapat={() => setYeniAcik(false)} eklendi={() => { setYeniAcik(false); yukle(); }} />
      )}
    </>
  );
}
